/**
 * Timing marks for comparing the map engines (M2.2): the same two numbers on
 * MappedIn and on Corridor, so a venue's baseline and its pilot are measured
 * the same way.
 *
 * - Map load: the map route entered (navigation start, on a cold page load) →
 *   the engine ready, its first level drawn and `mapReady` emitted. Also init →
 *   ready, which leaves out the route's code and data loading.
 * - Update latency: an MQTT location message arriving → the first frame after
 *   that user's marker was moved to it. Users not drawn as individual markers
 *   (zoomed out to totals, another level) are not counted.
 *
 * Read them in the console with `__mapTimings.summary()`, or in the DevTools
 * Performance panel (User Timing `map:load` and `map:init`). A summary is
 * logged as `[map-engine] timing` at most once a minute while updates arrive.
 * Map loads also go to Sentry as `map.load` spans, sampled with the traces.
 */
import * as Sentry from "@sentry/vue";

/** The operator map's update budget (M2.2). */
const UPDATE_BUDGET_MS = 250;
/** Samples kept for the percentiles. */
const MAX_SAMPLES = 5000;
const MAX_LOADS = 20;
/** Older than this, a message was never drawn (dropped, or its user hidden). */
const STALE_MS = 10_000;
const SUMMARY_EVERY_MS = 60_000;

export interface MapLoadSample {
    engine: string;
    venue: string;
    /** From navigation start: the page was loaded on the map route. */
    cold: boolean;
    /** Route entered → engine ready, ms; null when the map was rebuilt without a route change. */
    loadMs: number | null;
    /** initMap started → engine ready, ms. */
    initMs: number | null;
    at: string;
}

export interface MapTimingSummary {
    engine: string | null;
    venue: string | null;
    loads: MapLoadSample[];
    updates: {
        count: number;
        p50: number | null;
        p95: number | null;
        p99: number | null;
        max: number | null;
        /** Share of updates drawn within the 250 ms budget, 0–1. */
        withinBudget: number | null;
        /** Messages never drawn within STALE_MS. */
        dropped: number;
    };
}

let routeEnteredAt: number | null = null;
let coldStart = false;
let initStartedAt: number | null = null;
let current: { engine: string; venue: string } | null = null;
const loads: MapLoadSample[] = [];

/** Arrival time of the newest undrawn location message, per user id. */
const pending = new Map<number, number>();
const latencies: number[] = [];
let dropped = 0;
let lastSummaryAt = 0;

function measure(name: string, start: number | null, end: number): void {
    if (start === null) return;
    try {
        performance.measure(name, { start, end });
    } catch {
        // User Timing with explicit times is missing in very old browsers.
    }
}

const round = (ms: number | null) => (ms === null ? null : Math.round(ms));

/**
 * The map route was entered. `cold` when this is the page's first navigation,
 * so the load is counted from navigation start, as a user sees it.
 */
export function mapRouteEntered(cold: boolean): void {
    coldStart = cold;
    routeEnteredAt = cold ? 0 : performance.now();
}

/** The map component started building an engine. */
export function mapInitStarted(): void {
    initStartedAt = performance.now();
}

/** The engine drew its first level and the component emitted `mapReady`. */
export function mapEngineReady(engine: string, venue: string): void {
    const now = performance.now();
    const loadMs = routeEnteredAt === null ? null : now - routeEnteredAt;
    const initMs = initStartedAt === null ? null : now - initStartedAt;
    measure("map:load", routeEnteredAt, now);
    measure("map:init", initStartedAt, now);

    const sample: MapLoadSample = {
        engine,
        venue,
        cold: coldStart,
        loadMs: round(loadMs),
        initMs: round(initMs),
        at: new Date().toISOString(),
    };
    loads.push(sample);
    if (loads.length > MAX_LOADS) loads.shift();
    console.info(
        `[map-engine] timing: ${engine} map of ${venue} ready in ${sample.loadMs ?? "?"} ms from the route (${coldStart ? "cold" : "warm"}), ${sample.initMs ?? "?"} ms from init`,
    );

    if (routeEnteredAt !== null) {
        Sentry.startInactiveSpan({
            name: "map.load",
            op: "map.load",
            startTime: new Date(performance.timeOrigin + routeEnteredAt),
            forceTransaction: true,
            attributes: {
                "map.engine": engine,
                "map.venue": venue,
                "map.cold": coldStart,
                "map.init_ms": sample.initMs ?? undefined,
            },
        }).end(new Date(performance.timeOrigin + now));
    }

    // A new map starts a new run: earlier updates belonged to the old one.
    if (current?.engine !== engine || current?.venue !== venue) resetUpdates();
    current = { engine, venue };
    lastSummaryAt = now;
    routeEnteredAt = null;
    initStartedAt = null;
}

/** Location messages for these users arrived from MQTT. */
export function locationsReceived(userIds: Iterable<number>): void {
    const now = performance.now();
    for (const id of userIds) pending.set(id, now);
}

/**
 * These users' markers were just moved. The time is taken on the next frame,
 * when the move is drawn. `shown` limits it to users drawn as markers.
 */
export function markersMoved(
    userIds: Iterable<number>,
    shown?: (id: number) => boolean,
): void {
    if (pending.size === 0) return;
    const arrivals: number[] = [];
    for (const id of userIds) {
        const at = pending.get(id);
        if (at === undefined) continue;
        pending.delete(id);
        if (shown && !shown(id)) continue;
        arrivals.push(at);
    }
    if (arrivals.length === 0) return;
    requestAnimationFrame(() => {
        const now = performance.now();
        for (const at of arrivals) {
            const ms = now - at;
            if (ms > STALE_MS) {
                dropped += 1;
                continue;
            }
            latencies.push(ms);
        }
        if (latencies.length > MAX_SAMPLES) {
            latencies.splice(0, latencies.length - MAX_SAMPLES);
        }
        if (now - lastSummaryAt >= SUMMARY_EVERY_MS) {
            lastSummaryAt = now;
            prune(now);
            const { updates } = mapTimingSummary();
            console.info(
                `[map-engine] timing: ${current?.engine ?? "?"} updates on ${current?.venue ?? "?"}: p50 ${updates.p50} ms, p95 ${updates.p95} ms, max ${updates.max} ms, ${Math.round((updates.withinBudget ?? 0) * 100)}% within ${UPDATE_BUDGET_MS} ms (${updates.count} samples, ${updates.dropped} never drawn)`,
            );
        }
    });
}

/** Forgets messages that were never drawn. */
function prune(now: number): void {
    for (const [id, at] of pending) {
        if (now - at > STALE_MS) {
            pending.delete(id);
            dropped += 1;
        }
    }
}

function percentile(sorted: number[], p: number): number | null {
    if (sorted.length === 0) return null;
    const index = Math.ceil((p / 100) * sorted.length) - 1;
    return Math.round(sorted[Math.min(sorted.length - 1, Math.max(0, index))]);
}

export function mapTimingSummary(): MapTimingSummary {
    const sorted = [...latencies].sort((a, b) => a - b);
    const within = sorted.filter((ms) => ms <= UPDATE_BUDGET_MS).length;
    return {
        engine: current?.engine ?? null,
        venue: current?.venue ?? null,
        loads: [...loads],
        updates: {
            count: sorted.length,
            p50: percentile(sorted, 50),
            p95: percentile(sorted, 95),
            p99: percentile(sorted, 99),
            max: sorted.length ? Math.round(sorted[sorted.length - 1]) : null,
            withinBudget: sorted.length ? within / sorted.length : null,
            dropped,
        },
    };
}

function resetUpdates(): void {
    latencies.length = 0;
    pending.clear();
    dropped = 0;
}

/** Starts the update numbers afresh, keeping the load samples. */
export function resetMapTimings(): void {
    resetUpdates();
    lastSummaryAt = performance.now();
}

if (typeof window !== "undefined") {
    (
        window as unknown as {
            __mapTimings?: {
                summary: typeof mapTimingSummary;
                reset: typeof resetMapTimings;
            };
        }
    ).__mapTimings = { summary: mapTimingSummary, reset: resetMapTimings };
}
